import { useRecoilValue } from 'recoil';

import { Badge } from '@/components/badge';
import { RatesRouteBadge } from '@/components/modules/rates/route-badge';
import { Chart } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/loading/skeleton';
import { useMarketRates } from '@/hooks/use-market-rates';
import { formatMarketRateModelData } from '@/lib/models/rates/format-market-rate-data';
import { MarketRateQueryModel } from '@/lib/models/rates/types';
import { locationsState } from '@/state/locations';
import { getLocationName } from '@/utils/locations';
import {
  extractXenetaAdditionalFields,
  xenetaAirWeightBracketToString,
  xenetaCargoSizeBracketToString,
  xenetaContractLengthToString,
} from '@/utils/rates';
import { tv } from '@/utils/styles';

export interface RatesListRowProps {
  rateQuery: MarketRateQueryModel;
  isExample?: boolean;
  onRowClick: (row: MarketRateQueryModel) => void;
  variant?: 'default' | 'compact';
}

const getChangeColorStyles = (percentChange: number) => {
  if (percentChange === 0) return 'bg-grey-100/50 text-grey-400';
  return percentChange > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700';
};

const getChangeColor = (percentChange: number) => {
  if (percentChange === 0) return '#cacaca';
  return percentChange > 0 ? '#b91c1c' : '#2c780f';
};

const RatesListRow = ({ rateQuery, isExample, onRowClick, variant = 'default' }: RatesListRowProps) => {
  const locations = useRecoilValue(locationsState);

  const {
    ports: [{ pol, pod }],
    mode,
    label,
  } = rateQuery;

  const { containerTypes, weightBracket, contractLength } = extractXenetaAdditionalFields(rateQuery.additionalFields);

  const { rates, isSearching } = useMarketRates(rateQuery);

  // Locations
  const originLocation = pol ? locations[pol] : undefined;
  const destinationLocation = pod ? locations[pod] : undefined;
  const hasLocations = originLocation && destinationLocation;

  const {
    priceString,
    percentString,
    percentChange,
    hasRates,
    chart,
    lowPriceString,
    highPriceString,
    averagePriceString,
  } = formatMarketRateModelData(rates, mode);

  const { base, inner, cell, location, locationName, chartWrapper, chartBase, changeBadge } = styles({
    variant,
    hasRates,
  });

  if (isSearching) {
    return (
      <div className={inner()}>
        <div className={cell()}>
          <div className={location()}>
            <RatesRouteBadge label={label} mode={mode} variant="transparent" />
            <div className={locationName()}>Fetching latest rates...</div>
          </div>
        </div>
        <div className={cell({ className: 'bg-grey-50' })}>
          <Skeleton className="h-4 w-20" />
        </div>
        <div className={cell({ className: 'bg-grey-50' })}>
          <Skeleton className="h-4 w-20" />
        </div>
        <div className={cell({ className: 'optional bg-grey-50' })}>
          <Skeleton className="h-4 w-20" />
        </div>
        <div className={cell({ className: 'optional bg-grey-50' })}>
          <Skeleton className="h-4 w-20" />
        </div>
        <div className={cell({ className: 'optional bg-grey-50' })}>
          <Skeleton className="h-4 w-20" />
        </div>
        <div className={cell({ className: 'optional bg-grey-50' })}>
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    );
  }

  return (
    <div className={base()}>
      <button className={inner()} onClick={() => onRowClick(rateQuery)} type="button">
        <div className={cell()}>
          <div className={location()}>
            <RatesRouteBadge label={label} mode={mode} variant="transparent" />

            <div className={locationName()}>
              {hasLocations ? `${getLocationName(originLocation)} to ${getLocationName(destinationLocation)}` : label}
            </div>
          </div>
          {isExample && (
            <Badge variant="alert" className="absolute right-6 top-3.5 hidden py-0.5 md:block">
              Example
            </Badge>
          )}
        </div>
        {hasRates ? (
          <div className={cell({ className: 'text-baseSm font-medium md:pl-6 md:text-base' })}>{priceString}</div>
        ) : (
          <div className={cell({ className: 'pl-6' })}>No rates available</div>
        )}
        {hasRates ? (
          <div className={cell({ className: 'flex items-center gap-4 max-md:px-0' })}>
            <div
              className={changeBadge({
                className: getChangeColorStyles(percentChange),
              })}
            >
              <span>{percentString}</span>
              <div className={chartWrapper()}>
                <Chart
                  className={chartBase()}
                  data={chart.data}
                  type="line"
                  showMarkerEnd={false}
                  chartConfig={{
                    value: {
                      color: getChangeColor(percentChange),
                      label: 'Value',
                    },
                  }}
                  settings={{
                    axes: {
                      xAxis: {
                        hide: true,
                      },
                      yAxis: {
                        hide: true,
                        domain: chart.range,
                      },
                    },
                    tooltip: {
                      hide: true,
                    },
                    grid: {
                      vertical: false,
                      horizontal: false,
                    },
                    series: {
                      type: 'monotone',
                      strokeWidth: 1.5,
                    },
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={cell({ className: 'text-grey-400' })}>--</div>
        )}
        <div className={cell({ className: 'optional' })}>{highPriceString}</div>
        <div className={cell({ className: 'optional' })}>{lowPriceString}</div>
        <div className={cell({ className: 'optional' })}>{averagePriceString}</div>
        <div className={cell({ className: 'optional' })}>
          <Badge className="py-0.5">
            {xenetaAirWeightBracketToString(weightBracket)}
            {xenetaCargoSizeBracketToString(containerTypes)} / {xenetaContractLengthToString(contractLength)}
          </Badge>
        </div>
      </button>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'group cursor-pointer hover:bg-grey-50',
    inner:
      'grid min-h-[72px] w-full grid-cols-[1fr_0.75fr_1fr] items-center text-left text-sm leading-tight lg:grid-cols-[2.5fr,repeat(2,1.5fr),repeat(4,1fr)] xl:text-baseSm [&_.optional]:hidden lg:[&_.optional]:flex',
    cell: 'relative flex h-full min-w-0 items-center border-grey-200 px-2 py-4 md:px-4 lg:first:border-r-2',
    location: 'flex w-full min-w-0 flex-col pl-2 text-left',
    locationName: 'mt-2 hidden min-w-0 truncate text-xs text-grey-500 lg:block',
    changeBadge: 'flex items-center gap-2 rounded-md px-2 py-1 pl-3 font-medium',
    chartWrapper: 'h-8 w-16',
    chartBase: 'aspect-auto h-8 min-h-none',
  },
  variants: {
    hasRates: {
      true: null,
      false: { inner: 'text-grey-400' },
    },
    isHiddenOnMobile: {
      true: { cell: 'hidden lg:flex' },
      false: '',
    },
    variant: {
      compact: {
        base: 'rounded-md',
        inner: 'min-h-12 !grid-cols-[2fr_1fr_2fr] [&_.optional]:!hidden',
        cell: '!border-none px-2 py-2 first:pl-0',
        locationName: '!hidden',
        changeBadge: '!py-0',
      },
      default: null,
    },
  },
});

export { RatesListRow };
